// Permadeath + carried-supply-loss wiring for tactical-zone play.
//
// The V4 campaign keeps survivors in `survivorsById` and treats status "dead"
// as the durable sentinel. This module deliberately only depends on that plain
// data shape, so supply-run outcome resolution does not import the old V3 campaign
// module graph.
//
// tactical-turn.mjs's contact resolution (an enemy touching a unit) emits a
// domain-agnostic { type: "unit-lost", unitId, survivorId, coordinate,
// inventory } log entry; this module is where that effect actually gets
// applied to campaign state, called by the app layer when a tactical session
// ends.

export function applySurvivorDeath(campaign, survivorId, { cause, coordinate, gameMinutes } = {}) {
  const survivor = campaign?.survivorsById?.[survivorId] ?? null;
  if (!survivor) return campaign;
  return {
    ...campaign,
    survivorsById: {
      ...campaign.survivorsById,
      [survivorId]: {
        ...survivor,
        status: "dead",
        deathCause: cause ?? "tactical-contact",
        diedAtCoordinate: Array.isArray(coordinate) ? [...coordinate] : null,
        diedAtGameMinute: Number.isFinite(gameMinutes) ? gameMinutes : null,
      },
    },
  };
}

// Applies every "unit-lost" entry in a resolved tactical session's log to
// campaign state, and reports each extracted unit's banked items (real named
// supplies — see items.mjs) vs. a dead unit's items, which are simply never
// reported as banked (never included in `extractions`), not removed by a
// separate step.
export function resolveTacticalZoneOutcome(campaign, tacticalState) {
  let next = campaign;
  for (const effect of tacticalState.log) {
    if (effect.type !== "unit-lost") continue;
    next = applySurvivorDeath(next, effect.survivorId, {
      cause: "tactical-contact",
      coordinate: effect.coordinate,
      gameMinutes: tacticalState.turnNumber,
    });
  }

  const extractions = tacticalState.log
    .filter((effect) => effect.type === "unit-extracted")
    .map((effect) => ({
      unitId: effect.unitId,
      survivorId: effect.survivorId,
      items: [...(effect.inventory ?? [])],
      ...(effect.equipmentItemIds ? { equipmentItemIds: [...effect.equipmentItemIds] } : {}),
      ...(effect.health ? { health: { ...effect.health } } : {}),
    }));

  return { campaign: next, extractions, deadUnitIds: [...tacticalState.deadUnitIds] };
}
